import { type InfiniteData, type QueryKey, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { chunk } from 'lodash';
import { useCallback } from 'react';

import { type Board, type Library, type ListResponse, SortDirection, type SubnavBoardSort } from '@air/api/types';

import {
  getLibraryRootBoardsKey,
  LIBRARY_BOARDS_LIST,
} from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { useFavoriteBoardsCacheUpdate } from '~/components/v2/hooks/useFavoriteBoardsCacheUpdate';
import { useSubBoardsCacheUpdate } from '~/components/v2/hooks/useSubBoardsCacheUpdate';
import { getFavoriteBoardsKey } from '~/swr-hooks/boards/useFavoriteBoardsList';
import { BOARD_SUBBOARDS, getBoardSubBoardsKey } from '~/swr-hooks/boards/useSubBoardsList';
import { getWorkspaceBoardsKey } from '~/swr-hooks/boards/useWorkspaceBoards';
import { useGetSubnavSortValue } from '~/swr-hooks/subnav/useSubnavSort';
import { insertIf } from '~/utils/insertIf';
import { getOrderedBoards } from '~/utils/mutateUtils/getOrderedBoards';
import {
  defaultBoardsState,
  getRearrangedBoards,
  getUpdatedBoards,
  type UpdatedBoard,
} from '~/utils/mutateUtils/mutators';

type SetSideNavQueryDataParams = {
  cacheKeys: QueryKey[];
  updater: (data?: InfiniteData<ListResponse<Board>>) => InfiniteData<ListResponse<Board>> | undefined;
  revalidate?: boolean;
};

const useSetSideNavQueryData = () => {
  const queryClient = useQueryClient();

  const setSideNavQueryData = useCallback(
    ({ cacheKeys, updater, revalidate }: SetSideNavQueryDataParams) => {
      cacheKeys.forEach((key) => {
        queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>({ queryKey: key }, updater);
      });

      if (revalidate) {
        return Promise.all(cacheKeys.map(async (key) => queryClient.invalidateQueries({ queryKey: key })));
      }
    },
    [queryClient],
  );

  return {
    setSideNavQueryData,
  };
};

export const removeBoards =
  (boardIds: string[]) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    if (!data) return;

    return produce(data, (draft) => {
      draft.pages.forEach((page) => {
        page.data = page.data.filter((b) => !boardIds.includes(b.id));
      });
    });
  };

export const addBoards =
  (boards: Board[], boardSort: SubnavBoardSort = { name: '', direction: SortDirection.asc }) =>
  (data?: InfiniteData<ListResponse<Board>>): InfiniteData<ListResponse<Board>> | undefined => {
    return produce(data || defaultBoardsState, (draft) => {
      const flattenedBoards = draft.pages.flatMap((b) => b.data);
      flattenedBoards.push(...boards);

      const { orderedBoards } = getOrderedBoards(flattenedBoards, boardSort);
      const chunkedBoards = chunk(orderedBoards, Math.ceil(orderedBoards.length / draft?.pages.length));

      chunkedBoards.forEach((chunk, index) => {
        draft.pages[index].data = chunk;
      });
    });
  };

export const useUpdateSideNavBoard = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  /**
   * Use this method to update board in side nav
   */
  const updateSideNavBoard = useCallback(
    (updatedBoard: UpdatedBoard, subnavSortBy?: SubnavBoardSort) => {
      try {
        const subNavSort = getSubNavSortValue();

        const cacheKeys = [
          // update favorite boards if board is in favorites
          ...insertIf(updatedBoard.hasCurrentUser, () => [getFavoriteBoardsKey(updatedBoard.workspaceId)]),
          // update workspace boards if board has no parentId
          ...insertIf(!updatedBoard.parentId, () => [
            getWorkspaceBoardsKey({ workspaceId: updatedBoard.workspaceId, sortField: subNavSort?.boardSort }),
          ]),
          // update subboards if board has parentId
          ...insertIf(!!updatedBoard.parentId, () => {
            if (!updatedBoard.parentId) return [];
            // Sub-boards within a library use the library's sort
            const libraryId = updatedBoard.library?.id;
            const librarySortValue = libraryId ? getSubNavSortValue(libraryId) : undefined;
            const sortField = libraryId ? librarySortValue?.libraryBoardSorts?.[0] : subNavSort?.boardSort;
            return [getBoardSubBoardsKey(updatedBoard.parentId, sortField, libraryId)];
          }),
          // update library board if board has library
          ...insertIf(!!updatedBoard.library?.id, () => {
            if (!updatedBoard.library?.id) return [];
            const librarySortValue = getSubNavSortValue(updatedBoard.library.id);
            const librarySort = librarySortValue?.libraryBoardSorts?.[0];
            return [
              getLibraryRootBoardsKey({
                workspaceId: updatedBoard.workspaceId,
                libraryId: updatedBoard.library.id,
                sortField: librarySort,
              }),
            ];
          }),
        ];
        return setSideNavQueryData({ cacheKeys, updater: getUpdatedBoards(updatedBoard, subnavSortBy) });
      } catch (error) {
        console.error({ error });
      }
    },
    [getSubNavSortValue, setSideNavQueryData],
  );

  return {
    updateSideNavBoard,
  };
};

export const useRemoveBoardsFromSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();

  /**
   * Use this method to remove boards from side nav
   */
  const removeBoardsFromSideNav = useCallback(
    ({
      workspaceId,
      boardIds,
      parentId,
      libraryId,
      navSortParams,
    }: {
      workspaceId: string;
      boardIds: string[];
      /*
       Pass '*' to remove board from all places in sidenav, regardless of parentId or libraryId
       */
      parentId?: string | null | '*';
      navSortParams?: SubnavBoardSort;
      libraryId?: Library['id'];
    }) => {
      const cacheKeys = [
        getFavoriteBoardsKey(workspaceId),
        ...insertIf((!parentId && !libraryId) || parentId === '*', () => [
          getWorkspaceBoardsKey({ workspaceId, sortField: navSortParams }),
        ]),
        ...insertIf((!parentId || parentId === '*') && !!libraryId, () => [
          getLibraryRootBoardsKey({ libraryId, workspaceId, sortField: navSortParams }),
        ]),
        ...insertIf(parentId === '*' && !libraryId, () => [[LIBRARY_BOARDS_LIST]]),
        parentId && parentId !== '*' ? getBoardSubBoardsKey(parentId, navSortParams, libraryId) : [BOARD_SUBBOARDS],
      ];

      return setSideNavQueryData({ cacheKeys, updater: removeBoards(boardIds) });
    },
    [setSideNavQueryData],
  );

  return {
    removeBoardsFromSideNav,
  };
};

export const useToggleFavoritedSideNavBoard = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();

  const toggleFavoritedSideNavBoard = useCallback(
    (board: Board) => {
      const { workspaceId, hasCurrentUser, id } = board;

      if (hasCurrentUser) {
        setSideNavQueryData({ cacheKeys: [getFavoriteBoardsKey(workspaceId)], updater: addBoards([board]) });
      } else {
        setSideNavQueryData({ cacheKeys: [getFavoriteBoardsKey(workspaceId)], updater: removeBoards([id]) });
      }

      const keysToUpdate = [
        getWorkspaceBoardsKey({ workspaceId }),
        // TODO check if getLibraryRootBoardsKey should be added
        // by passing '*' we mutate all board subboards cache keys and don't need parentId
        [BOARD_SUBBOARDS],
      ];

      return setSideNavQueryData({ cacheKeys: keysToUpdate, updater: getUpdatedBoards({ id, hasCurrentUser }) });
    },
    [setSideNavQueryData],
  );

  return {
    toggleFavoritedSideNavBoard,
  };
};

export const useAddBoardsToSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();

  const addBoardsToSideNav = useCallback(
    (boards: Board[], boardSort?: SubnavBoardSort) => {
      const cacheKeys = [];
      const libraryId = boards[0].library?.id;

      // currently it is only possible to add multiple boards at once to one parent (by duplicating them)
      if (!boards[0].parentId && libraryId) {
        cacheKeys.push(
          getLibraryRootBoardsKey({
            workspaceId: boards[0].workspaceId,
            sortField: boardSort,
            libraryId: boards[0].library?.id,
          }),
        );
      } else if (!boards[0].parentId) {
        cacheKeys.push(
          getWorkspaceBoardsKey({
            workspaceId: boards[0].workspaceId,
            sortField: boardSort,
          }),
        );
      } else {
        cacheKeys.push(getBoardSubBoardsKey(boards[0].parentId, boardSort, libraryId));
      }

      return setSideNavQueryData({ cacheKeys, updater: addBoards(boards, boardSort) });
    },
    [setSideNavQueryData],
  );

  return {
    addBoardsToSideNav,
  };
};

export const useChangeSideBoardsParent = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  const changeSideBoardsParent = useCallback(
    async ({
      workspaceId,
      newBoards,
      oldParentId,
      oldLibraryId = undefined,
    }: {
      workspaceId: string;
      newBoards: Board[];
      oldParentId?: string | null;
      oldLibraryId?: string | undefined;
    }) => {
      try {
        if (!newBoards || newBoards.length === 0) {
          return;
        }

        const newParentId = newBoards[0].parentId;
        const newLibraryId = newBoards[0].library?.id;

        // Sub-boards within a library use the library's sort, otherwise use workspace sort
        const sortValue = getSubNavSortValue();
        const workspaceBoardSort = sortValue?.boardSort;

        // Get old location sort
        let oldBoardSort: SubnavBoardSort | undefined;
        if (oldParentId) {
          // Sub-boards use library sort if in a library, otherwise workspace sort
          if (oldLibraryId) {
            const oldLibrarySortValue = getSubNavSortValue(oldLibraryId);
            oldBoardSort = oldLibrarySortValue?.libraryBoardSorts?.[0];
          } else {
            oldBoardSort = workspaceBoardSort;
          }
        } else if (oldLibraryId) {
          // Root library boards use library-specific sort
          const oldLibrarySortValue = getSubNavSortValue(oldLibraryId);
          oldBoardSort = oldLibrarySortValue?.libraryBoardSorts?.[0];
        } else {
          // Root workspace boards use workspace sort
          oldBoardSort = workspaceBoardSort;
        }

        // Get new location sort
        let newBoardSort: SubnavBoardSort | undefined;
        if (newParentId) {
          // Sub-boards use library sort if in a library, otherwise workspace sort
          if (newLibraryId) {
            const newLibrarySortValue = getSubNavSortValue(newLibraryId);
            newBoardSort = newLibrarySortValue?.libraryBoardSorts?.[0];
          } else {
            newBoardSort = workspaceBoardSort;
          }
        } else if (newLibraryId) {
          // Root library boards use library-specific sort
          const newLibrarySortValue = getSubNavSortValue(newLibraryId);
          newBoardSort = newLibrarySortValue?.libraryBoardSorts?.[0];
        } else {
          // Root workspace boards use workspace sort
          newBoardSort = workspaceBoardSort;
        }

        let keyToRemove: QueryKey, keyToAdd: QueryKey;

        if (oldParentId) {
          keyToRemove = getBoardSubBoardsKey(oldParentId, oldBoardSort, oldLibraryId);
        } else if (oldLibraryId) {
          keyToRemove = getLibraryRootBoardsKey({ workspaceId, sortField: oldBoardSort, libraryId: oldLibraryId });
        } else {
          keyToRemove = getWorkspaceBoardsKey({ workspaceId, sortField: oldBoardSort });
        }

        if (newParentId) {
          keyToAdd = getBoardSubBoardsKey(newParentId, newBoardSort, newLibraryId);
        } else if (newLibraryId && !newParentId) {
          // Only use library key if it's a root board (no parent)
          keyToAdd = getLibraryRootBoardsKey({ workspaceId, sortField: newBoardSort, libraryId: newLibraryId });
        } else {
          keyToAdd = getWorkspaceBoardsKey({ workspaceId, sortField: newBoardSort });
        }

        return await Promise.all([
          setSideNavQueryData({ cacheKeys: [keyToRemove], updater: removeBoards(newBoards.map((board) => board.id)) }),
          setSideNavQueryData({ cacheKeys: [keyToAdd], updater: addBoards(newBoards, newBoardSort) }),
        ]);
      } catch (error) {
        console.error({ error });
      }
    },
    [getSubNavSortValue, setSideNavQueryData],
  );

  return {
    changeSideBoardsParent,
  };
};

export const useRearrangeBoardsInSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { getSubNavSortValue } = useGetSubnavSortValue();
  const { updateSubBoardsCache } = useSubBoardsCacheUpdate();
  const rearrangeBoardsInSideNav = useCallback(
    (rearrangedBoards: Board[]) => {
      const newParentId = rearrangedBoards[0].parentId;
      const newLibraryId = rearrangedBoards[0].library?.id;

      // Determine the correct sort to use
      let boardSort: SubnavBoardSort | undefined;
      if (newLibraryId) {
        // Library boards (both root and sub-boards) use library-specific sort
        const librarySortValue = getSubNavSortValue(newLibraryId);
        boardSort = librarySortValue?.libraryBoardSorts?.[0];
      } else {
        // Workspace boards use workspace sort
        boardSort = getSubNavSortValue()?.boardSort;
      }

      let keyToUpdate: QueryKey;

      if (newParentId) {
        keyToUpdate = getBoardSubBoardsKey(newParentId, boardSort, newLibraryId);

        updateSubBoardsCache({
          parentBoard: { id: newParentId },
          sort: boardSort,
          rearrangedBoards,
        });
      } else if (newLibraryId) {
        keyToUpdate = getLibraryRootBoardsKey({
          workspaceId: rearrangedBoards[0].workspaceId,
          sortField: boardSort,
          libraryId: newLibraryId,
        });
      } else {
        keyToUpdate = getWorkspaceBoardsKey({ workspaceId: rearrangedBoards[0].workspaceId, sortField: boardSort });
      }

      return setSideNavQueryData({ cacheKeys: [keyToUpdate], updater: getRearrangedBoards(rearrangedBoards) });
    },
    [getSubNavSortValue, setSideNavQueryData, updateSubBoardsCache],
  );

  return {
    rearrangeBoardsInSideNav,
  };
};

export const useRearrangeFavoriteBoardsInSideNav = () => {
  const { setSideNavQueryData } = useSetSideNavQueryData();
  const { updateFavoriteBoardsCache } = useFavoriteBoardsCacheUpdate();
  const rearrangeFavoriteBoardsInSideNav = useCallback(
    (rearrangedBoards: Board[]) => {
      setSideNavQueryData({
        cacheKeys: [getFavoriteBoardsKey(rearrangedBoards[0].workspaceId)],
        updater: getRearrangedBoards(rearrangedBoards),
      });

      updateFavoriteBoardsCache({ rearrangedBoards });
    },
    [setSideNavQueryData, updateFavoriteBoardsCache],
  );

  return {
    rearrangeFavoriteBoardsInSideNav,
  };
};

export const useRefreshSideNavBoards = () => {
  const queryClient = useQueryClient();
  const { getSubNavSortValue } = useGetSubnavSortValue();

  const refreshRootSideNavBoards = useCallback(
    (workspaceId: string) => {
      const subNavSort = getSubNavSortValue();
      return queryClient.invalidateQueries({
        queryKey: getWorkspaceBoardsKey({ workspaceId, sortField: subNavSort?.boardSort }),
      });
    },
    [getSubNavSortValue, queryClient],
  );

  const refreshSideNavSubBoards = useCallback(
    (boardId: string) => {
      const subNavSort = getSubNavSortValue();
      queryClient.invalidateQueries({ queryKey: getBoardSubBoardsKey(boardId, subNavSort?.boardSort) });
    },
    [getSubNavSortValue, queryClient],
  );

  return {
    refreshRootSideNavBoards,
    refreshSideNavSubBoards,
  };
};
